import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Filter, 
  X, 
  Calendar as CalendarIcon,
  ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskFilter = ({ onFilterChange, initialFilters = {} }) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || '');
  const [statusFilter, setStatusFilter] = useState(initialFilters.status || 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(initialFilters.assignee || 'all');
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom || null);
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || null);
  
  // Count active filters
  const activeFilterCount = [
    statusFilter !== 'all',
    assigneeFilter !== 'all',
    dateFrom !== null,
    dateTo !== null
  ].filter(Boolean).length;

  // Fetch users for assignee filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, [token]);

  // Debounce search term and trigger filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerFilterChange();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Trigger filter change immediately for dropdown/date changes
  useEffect(() => {
    triggerFilterChange();
  }, [statusFilter, assigneeFilter, dateFrom, dateTo]);

  const triggerFilterChange = () => {
    const filters = {
      search: searchTerm.trim(),
      status: statusFilter,
      assignee: assigneeFilter,
      dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : null,
      dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : null
    };
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAssigneeFilter('all');
    setDateFrom(null);
    setDateTo(null);
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || assigneeFilter !== 'all' || dateFrom || dateTo;

  return (
    <div className="space-y-4" data-testid="task-filter-container">
      {/* Search Bar */}
      <div className="flex gap-2">
        <Button
          variant={showFilters ? "secondary" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2"
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filters</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
              {activeFilterCount}
            </Badge>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </Button>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search tasks by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="task-search-input"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchTerm('')}
              data-testid="clear-search-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={handleClearFilters}
            className="text-muted-foreground hover:text-foreground"
            data-testid="clear-all-filters-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 border rounded-lg bg-card" data-testid="expanded-filters">
          {/* Status Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assignee Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Assignee</Label>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger data-testid="assignee-filter-select">
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date From */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Due Date From</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="date-from-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
                {dateFrom && (
                  <div className="p-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setDateFrom(null)}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* Date To */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Due Date To</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="date-to-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
                {dateTo && (
                  <div className="p-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setDateTo(null)}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && !showFilters && (
        <div className="flex flex-wrap gap-2" data-testid="active-filters-display">
          {searchTerm && (
            <Badge variant="secondary" className="flex items-center gap-1 transition-all duration-150 hover:bg-muted hover:scale-105 cursor-pointer">
              Search: "{searchTerm}"
              <X 
                className="w-3 h-3 cursor-pointer hover:text-destructive" 
                onClick={() => setSearchTerm('')}
              />
            </Badge>
          )}
          {statusFilter !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1 transition-all duration-150 hover:bg-muted hover:scale-105 cursor-pointer">
              Status: {statusFilter}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-destructive" 
                onClick={() => setStatusFilter('all')}
              />
            </Badge>
          )}
          {assigneeFilter !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1 transition-all duration-150 hover:bg-muted hover:scale-105 cursor-pointer">
              Assignee: {users.find(u => u.id === assigneeFilter)?.name || assigneeFilter}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-destructive" 
                onClick={() => setAssigneeFilter('all')}
              />
            </Badge>
          )}
          {dateFrom && (
            <Badge variant="secondary" className="flex items-center gap-1 transition-all duration-150 hover:bg-muted hover:scale-105 cursor-pointer">
              From: {format(dateFrom, 'MMM d, yyyy')}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-destructive" 
                onClick={() => setDateFrom(null)}
              />
            </Badge>
          )}
          {dateTo && (
            <Badge variant="secondary" className="flex items-center gap-1 transition-all duration-150 hover:bg-muted hover:scale-105 cursor-pointer">
              To: {format(dateTo, 'MMM d, yyyy')}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-destructive" 
                onClick={() => setDateTo(null)}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskFilter;
